import axios from 'axios';
import type {
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
    InternalAxiosRequestConfig,
    AxiosError,
} from 'axios';

// request 方法 opts 参数的接口
export interface IRequestOptions extends InternalAxiosRequestConfig {
    skipErrorHandler?: boolean;
}
interface UseFunctionParam<T> {
    onFulfilled?: (value: T) => T | Promise<T>;
    onRejected?: (error: any) => any;
}
type IRequestInterceptorAxios = (
    config: IRequestOptions,
) => IRequestOptions | Promise<IRequestOptions>;
type IRequestInterceptor = IRequestInterceptorAxios;
type IErrorInterceptor = (error: Error) => Promise<Error>;
type IResponseInterceptor = <T = any>(
    response: AxiosResponse<T>,
) => AxiosResponse<T>;
type IRequestInterceptorTuple =
    | [IRequestInterceptor, IErrorInterceptor]
    | [IRequestInterceptor]
    | IRequestInterceptor;
type IResponseInterceptorTuple =
    | [IResponseInterceptor, IErrorInterceptor]
    | [IResponseInterceptor]
    | IResponseInterceptor;

export interface RequestConfig<T = any> extends AxiosRequestConfig {
    // 返回状态参数校验，自定义校验规则： true|'' —— 校验成功，false|其他字符串｜抛错 —— 校验失败，在无response的message时会将结果作为reason
    responseVerify?: (response: AxiosResponse) => boolean | string | never;
    interceptors?: {
        request?: UseFunctionParam<InternalAxiosRequestConfig>[];
        response?: UseFunctionParam<AxiosResponse>[];
    };
    requestInterceptors?: IRequestInterceptorTuple[];
    responseInterceptors?: IResponseInterceptorTuple[];
    logger?: any;
}

/**
 * interceptors.request 定义在后面的先执行
 * interceptors.response 定义在前面的先执行
 */
export const pcRequest = (
    config: AxiosRequestConfig,
    requestConfig: RequestConfig,
): AxiosInstance => {
    const instance = axios.create({
        timeout: 15000, // 超时配置
        withCredentials: true, // 跨域携带cookie
        ...config, // 自定义配置覆盖基本配置
    });

    const logTaskMap = new WeakMap();

    // 添加请求拦截器（埋点使用）
    instance.interceptors.request.use(
        function (config: any) {
            if (requestConfig.logger) {
                const logTask = requestConfig.logger.asyncTask({
                    name: 'API request',
                    extra_info: { options: config },
                });
                logTaskMap.set(config, logTask);
            }

            return config;
        },
        function (error) {
            // 对请求错误处理
            return Promise.reject(error);
        },
    );

    const { response = [], request = [] } = requestConfig?.interceptors ?? {};
    response.forEach(({ onFulfilled, onRejected }) => {
        instance.interceptors.response.use(onFulfilled, onRejected);
    });
    request.forEach(({ onFulfilled, onRejected }) => {
        instance.interceptors.request.use(onFulfilled, onRejected);
    });
    // 初始化配置请求拦截器
    requestConfig?.requestInterceptors?.forEach((interceptor) => {
        if (interceptor instanceof Array) {
            instance.interceptors.request.use((config) => {
                return interceptor[0](config);
            }, interceptor[1]);
        } else {
            instance.interceptors.request.use((config) => {
                return interceptor(config);
            });
        }
    });
    // 初始化配置响应拦截器
    requestConfig?.responseInterceptors?.forEach((interceptor) => {
        interceptor instanceof Array
            ? instance.interceptors.response.use(interceptor[0], interceptor[1])
            : instance.interceptors.response.use(interceptor);
    });

    // 防护拦截
    // https://docs.corp.kuaishou.com/d/home/fcAAeoT8TGSrLvfahH3Itox6P#section=h.ma2xkflqfvwd
    instance.interceptors.response.use((response) => {
        const data = response.data;
        if ([data?.code, data?.result].includes(400010)) {
            const message =
                data.errMsg ||
                data.errorMsg ||
                data.error_msg ||
                '访问太快，请稍候再试。';

            data.message = message;
            return Promise.reject(response);
        }
        return response;
    });

    // 请求校验
    instance.interceptors.response.use(
        (response) => {
            try {
                // 请求码判断
                if (response.status !== 200) {
                    throw new Error(response.statusText || '无效的状态码');
                }

                const responseVerify =
                    requestConfig?.responseVerify || (() => true);
                const valid = responseVerify?.(response?.data || {});
                if (!['', true].includes(valid)) {
                    let message: string = response?.data?.message;
                    if (!message) {
                        message =
                            typeof valid === 'string' ? valid : '无效的状态码';
                    }
                    if (!message) {
                        message = '无效的状态码';
                    }
                    throw new Error(message);
                }
            } catch (err: any) {
                response.data.message = err.message;
                return Promise.reject(response);
            }

            return response;
        },
        (error: AxiosError) => {
            // 网络错误
            const { status, message } = error;
            if (status !== 200) {
                return Promise.reject({
                    ...error,
                    data: {
                        status,
                        message,
                    },
                });
            }
            return Promise.reject(error);
        },
    );

    // 添加响应拦截器(埋点使用)
    instance.interceptors.response.use(
        function (response) {
            // 对响应数据处理
            if (requestConfig.logger) {
                const logTask = logTaskMap.get(response.config);
                if (logTask) {
                    logTaskMap.delete(response.config);
                    logTask
                        .next({
                            extra_info: {
                                data: response,
                                status: 'success',
                            },
                        })
                        .end();
                }
            }

            return response.data;
        },
        function (error) {
            if (requestConfig.logger) {
                const logTask = logTaskMap.get(error.config);
                if (logTask) {
                    logTaskMap.delete(error.config);
                    logTask
                        .next({
                            extra_info: {
                                data: error,
                                status: 'fail',
                            },
                        })
                        .end();
                }
            }
            return Promise.reject(error.data);
        },
    );

    return instance;
};

export default pcRequest;
